import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'

import { fetchChargingLocation } from '../api'
import { fetchChargingLocationQueryKey } from '../functions'
import { TimeInMilliseconds } from 'src/Libs/types'
import { ChargingLocationModel } from '../models'
import { usePortalQuery } from 'src/Libs/api'

interface UseFetchChargingLocation {
  chargingLocation: ChargingLocationModel | null
  loading: boolean
  error?: Error | AuthFetchError
  hasError: boolean
}

/* Infinity: data is considered always fresh. (ie. no refetch) */
const STALE_TIME = Infinity
const MAX_RETRY_ATTEMPTS = 2
const MAX_RETRY_INTERVAL = TimeInMilliseconds.thirtySecond

/**
 * Hook for handling fetching of vehicle information of user
 */
export const useFetchChargingLocation = (installationId: number | undefined): UseFetchChargingLocation => {
  const authFetch = useAuthFetchWithResponse()
  const queryKey = fetchChargingLocationQueryKey(installationId)

  const queryFn = async (): Promise<ChargingLocationModel | null> => {
    const { error: fetchError, hasErrors, value } = await fetchChargingLocation(installationId, authFetch)

    if (hasErrors || fetchError) {
      throw new Error(fetchError?.message)
    }
    return value
  }

  const { data, isLoading, isError } = usePortalQuery({
    queryKey: queryKey,
    queryFn,
    /* disable query if installationId is undefined */
    enabled: installationId !== undefined,
    retry: MAX_RETRY_ATTEMPTS,
    /* retry using exponential backoff if request fails */
    retryDelay: attempt => {
      return Math.min(500 * 2 ** attempt, MAX_RETRY_INTERVAL)
    },
    staleTime: STALE_TIME,
  })

  return { chargingLocation: data ?? null, loading: isLoading, hasError: isError }
}
